import * as React from 'react'
import { Paper, Stepper, Step, StepLabel, Backdrop, CircularProgress } from '@material-ui/core'
import FillButton from 'components/presenter/buttons/FillButton'
import PlatButton from 'components/presenter/buttons/PlatButton'
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next'
import cn from 'classnames'
import { useStore, newStoreId } from 'stores'
import ChooseShopForm from 'components/container/page/jetso/ChooseShopForm'
import FillJetsoDetailForm from 'components/container/page/jetso/FillJetsoDetailForm'
import UploadLeafletForm from 'components/container/page/jetso/UploadLeafletForm'
import { useSnackbar } from 'libs/ui/snackbar'
import * as styles from './create.module.css'

const CreateJetsoPage = () => {
  const { t } = useTranslation()
  const { navigate } = useI18next()
  const { showSuccess } = useSnackbar()
  const newsStore = useStore(newStoreId)
  const [isLoading, setLoading] = React.useState(false)
  const [activeStep, setActiveStep] = React.useState(0)
  const [selectedLocations, setSelectedLocations] = React.useState([])
  const [selectedFiles, setSelectedFiles] = React.useState([])
  const [detailFormData, setDetailFormData] = React.useState({
    isFormReady: false,
    data: {
      category: [],
      keyword: [],
      tags: [],
      promoteStartAt: null,
      promoteEndAt: null,
      publishAt: null,
      meta: {
        artist: [],
        voucher: [],
        care: [],
        age: [],
        gender: []
      }
    }
  })

  const steps = [
    t('jetso.chooseShop'),
    t('jetso.uploadLeaflet'),
    t('jetso.fillJetsoDetail')
  ]

  const handleScrollTop = () => {
    window.scrollTo(0, 0)
  }

  const onBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1)
      handleScrollTop()
    }
  }

  const triggerCreateNewsPost = async () => {
    const {data} = detailFormData
    setLoading(true)

    const metaList = data.meta.gender.concat(data.meta.age, data.meta.care, data.meta.voucher, data.meta.artist)

    const response = await newsStore.actions.createNewsPost({
      title: data.title,
      categoryShortCodes: data.category.map(c => c.short_code),
      tags: data.tags,
      keywordShortCodes: data.keyword.map(k => k.short_code),
      promoteStartAt: data.promoteStartAt,
      promoteEndAt: data.promoteEndAt,
      publishAt: data.publishAt,
      url: data.website,
      description: data.description,
      locationIds: selectedLocations.map(location => location._id),
      metaCodes: metaList.map(m => m.short_code),
    }, selectedFiles.map(file => file.crop.blob))
    setLoading(false)

    if (!response.error) {
      showSuccess(t('common.success'))
      navigate(`/jetso/`)
    }
  }

  const onNext = async () => {
    if (activeStep >= 2) {
      await triggerCreateNewsPost()
    } else {
      setActiveStep(activeStep + 1)
      handleScrollTop()
    }
  }

  const handdleSelectAllLocation = selected => {
    setSelectedLocations(selected)
  }

  const handleSelectLocation = (selectedRow, checked) => {
    const selectedIndex = selectedLocations.indexOf(selectedRow)
    let newSelected = []
    if (selectedIndex === -1) {
      if (checked) {
        newSelected = newSelected.concat(selectedLocations, selectedRow)
      }
    } else if (!checked) {
      newSelected = [...selectedLocations]
      newSelected.splice(selectedIndex, 1)
    }
    setSelectedLocations(newSelected)
  }

  const isNextEnabled = () => {
    if (activeStep === 0) {
      return selectedLocations.length > 0
    } 
    if (activeStep === 1) {
      return selectedFiles.length > 0
    }
    return detailFormData.isFormReady
  }

  const onChangedFiles = (list) => {
    setSelectedFiles(list)
  }

  const onDetailDataChanged = (data) => {
    setDetailFormData(data)
  }

  const activeForm = () => {
    if (activeStep === 0) {
      return (
        <ChooseShopForm
          selected={selectedLocations}
          onSelectAll={handdleSelectAllLocation}
          onSelect={handleSelectLocation}
        />
      )
    } 
    if (activeStep === 1) {
      return (
        <UploadLeafletForm
          selectedFiles={selectedFiles}
          onChanged={onChangedFiles}
        />
      )
    }
    return <FillJetsoDetailForm 
              initFormData={detailFormData.data}
              onChanged={onDetailDataChanged}/>
  }

  return (
    <>
      <Stepper className="mui-stepper" activeStep={activeStep} alternativeLabel>
        {steps.map((label,) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <div className={styles.content}>
        {activeForm()}
      </div>
      <Paper className={cn(styles.bottomBar)} elevation={2}>
        {
          activeStep !== 0 && 
          <FillButton 
            className={cn(styles.btnUpdate)}
            onClick={onBack}
          >
            {t('common.back')}
          </FillButton>
        }
        <div className={styles.space} />
        {
          activeStep === 0 && 
          <PlatButton
            className={styles.btnSkip}
            onClick={onNext}
          >
            {t('common.skip')}
          </PlatButton>
        }
        <FillButton 
          className={cn(styles.btnUpdate)}
          disabled={!isNextEnabled()}
          onClick={onNext}
        >
          {t(activeStep === 2 ? 'common.submit' : 'common.next')}
        </FillButton>
      </Paper>
      <Backdrop className='mui-backdrop' open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  )
}

export default CreateJetsoPage